import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BrandsSection from '@/components/BrandsSection';
import BrandContact from '@/components/BrandContact';
import AnimatedBackground from '@/components/AnimatedBackground';
import { useLanguage } from '@/App';
import { XCircle, CheckCircle2, Quote } from 'lucide-react';

const AboutPage = () => {
  const { language } = useLanguage();

  const content = {
    tr: {
      title: 'Hakkımızda',
      intro: 'Nisan 2023\'te bir fikrimiz vardı:',
      quote: '"Influencer pazarlama böyle yapılmamalı. Markalar daha iyisini hak ediyor."',
      problems: [
        'Sadece takipçi sayısına bakarak influencer seçmek.',
        'Her kampanyada aynı isimleri önermek.',
        'Hiçbir öngörü sunmadan bütçeleri "deneyelim görelim" mantığıyla harcamak.',
        'Adil olmayan kâr marjlarıyla iş yapmak.'
      ],
      changeText: 'Bunları değiştirmek istedik.',
      anniversary: 'Nisan 2026\'da Persona Ajans 3. yılını doldurdu. Bu süreçte:',
      achievements: [
        'ebebek, Braun, Tecno Mobile, Macro Center gibi 25\'ten fazla kurumsal marka ile çalıştık.',
        'Yüzlerce influencer ile binlerce içerik ürettik.',
        'Kampanyalarımız on milyonlarca izlenmeye ulaştı.',
        'Ve en önemlisi: Markalar için influencer kararlarını vermeden önce sonucunu öngörebilmelerine yardımcı olan OPARA\'yı geliştirdik.'
      ],
      oparaText: 'OPARA, veriye dayalı influencer analiz sistemimiz, 3. yıl dönümümüzde Nisan 2026\'da önce Türkiye\'ye, ardından global pazara B2B SaaS platformu olarak açılıyor. Çünkü markalar "umarım iyi olur" değil, "bu kadar izlenme gelecek" veya "bu kadar link tıklaması gelecek" duymayı istiyor ve hak ediyor.',
      brandsTitle: 'Çalıştığımız Markalardan Örnekler',
      brandsDescription: 'Birlikte büyüdüğümüz markalarla gurur duyuyoruz. Her biri için özel stratejiler geliştiriyor, dijital dünyada güçlü bir varlık oluşturuyoruz.'
    },
    en: {
      title: 'About Us',
      intro: 'In April 2023, we had an idea:',
      quote: '"Influencer marketing shouldn\'t be done this way. Brands deserve better."',
      problems: [
        'Selecting influencers based only on follower count.',
        'Recommending the same names for every campaign.',
        'Spending budgets with a "let\'s try and see" mentality without any forecasting.',
        'Working with unfair profit margins.'
      ],
      changeText: 'We wanted to change this.',
      anniversary: 'In April 2026, Persona Agency completed its 3rd year. During this time:',
      achievements: [
        'We worked with more than 25 corporate brands like ebebek, Braun, Tecno Mobile, Macro Center.',
        'We produced thousands of content with hundreds of influencers.',
        'Our campaigns reached tens of millions of views.',
        'And most importantly: We developed OPARA to help brands predict outcomes before making influencer decisions.'
      ],
      oparaText: 'OPARA, our data-driven influencer analysis system, is launching as a B2B SaaS platform first in Turkey, then to global markets on our 3rd anniversary in April 2026. Because brands want and deserve to hear "this many views will come" or "this many link clicks will come" instead of "hopefully it will be good".',
      brandsTitle: 'Examples of Brands We Work With',
      brandsDescription: 'We are proud of the brands we grow together with. We develop special strategies for each of them and create a strong presence in the digital world.'
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-black relative flex flex-col">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col min-h-screen">
        <Navbar />
        
        {/* About Content */}
        <div className="px-4 pt-32 pb-8 bg-black bg-opacity-75">
          <div className="max-w-4xl mx-auto">
            {/* Title */}
            <div className="text-center mb-12 animate-slide-in-left">
              <h1 className="text-3xl md:text-4xl text-white font-bold tracking-tight">
                {t.title}
              </h1>
            </div>

            {/* Story Content — section'lar arasi olculu nefes (sikistirildi: paragraf akisi hissi) */}
            <div className="space-y-8 text-left">
              {/* Intro */}
              <p className="text-lg md:text-xl text-white leading-relaxed">
                {t.intro}
              </p>

              {/* Quote — sari box, sol tirnak ikonu + italic vurgu, padding p-8 */}
              <div className="relative bg-gray-900/30 border-l-4 border-[#FFDD59] p-8 rounded-r-lg">
                <Quote
                  size={32}
                  className="absolute top-4 left-4 text-[#FFDD59]/40"
                  aria-hidden="true"
                />
                <p className="text-xl md:text-2xl text-[#FFDD59] font-medium italic leading-relaxed pl-10">
                  {t.quote}
                </p>
              </div>

              {/* Problems List — XCircle (text-red-400, size 24), gap-3, text-lg */}
              <ul className="space-y-2.5 text-gray-300 text-lg">
                {t.problems.map((problem, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <XCircle size={24} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <span className="leading-relaxed">{problem}</span>
                  </li>
                ))}
              </ul>

              {/* Change Text — vurgu korunur (text-2xl), dikey alan olculu (my-10) */}
              <div className="my-10">
                <p className="text-2xl text-white font-semibold text-center tracking-tight">
                  {t.changeText}
                </p>
              </div>

              {/* Anniversary */}
              <p className="text-lg md:text-xl text-[#FFDD59] font-semibold leading-relaxed">
                {t.anniversary}
              </p>

              {/* Achievements List — CheckCircle2 (text-[#FFDD59], size 24), gap-3, text-lg */}
              <ul className="space-y-2.5 text-gray-300 text-lg">
                {t.achievements.map((achievement, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 size={24} className="text-[#FFDD59] mt-0.5 flex-shrink-0" />
                    <span className="leading-relaxed">{achievement}</span>
                  </li>
                ))}
              </ul>

              {/* OPARA Text — sari sicak ton, text-lg leading-relaxed */}
              <div
                className="relative bg-gray-900/50 border border-[#FFDD59]/30 p-6 mt-8 overflow-hidden"
                style={{ borderRadius: '14px' }}
              >
                {/* Gold gradient top line */}
                <div
                  className="absolute top-0 left-0 right-0 h-[2px]"
                  style={{
                    background: 'linear-gradient(90deg, transparent, #F5C518, transparent)'
                  }}
                />
                <p className="text-lg text-gray-200 leading-relaxed">
                  {t.oparaText}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Brands Section */}
        <BrandsSection showTitle={true} titleText={t.brandsTitle} />

        {/* Contact Form — id="brand-form" Footer'daki "Marka Talep Formu" linki icin anchor hedefi */}
        <section id="brand-form" className="scroll-mt-24">
          <BrandContact />
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default AboutPage;
